import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@nrwl/environment';
import { catchError, map, MonoTypeOperatorFunction, Observable, throwError } from 'rxjs';
import { AmendmentLock, AmendmentLockSurface } from '@nrwl/rx-models';

/**
 * Thrown when acquiring, refreshing or amending fails because another user holds the lock (409).
 */
export class AmendmentLockConflictError extends Error {
  constructor(
    public readonly code: string,
    public readonly detail: string,
    public readonly lock: AmendmentLock | null,
  ) {
    super(detail);
  }
}

/**
 * Maps a 409 amendment-lock response to {@link AmendmentLockConflictError}; other errors pass
 * through unchanged. Shared by every endpoint that can reject with the lock conflict body.
 */
export function rethrowAmendmentLockConflict<T>(): MonoTypeOperatorFunction<T> {
  return catchError((error: HttpErrorResponse) => {
    if (error.status === 409) {
      return throwError(() => parseAmendmentLockConflictError(error.error));
    }

    return throwError(() => error);
  });
}

/**
 * API service for reading, acquiring and refreshing the document-level amendment lock.
 *
 * There is no explicit release endpoint: the backend releases the lock when the amendment is
 * completed and expires it after 15 minutes without a heartbeat.
 */
@Injectable({ providedIn: 'root' })
export class AmendmentLockService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.baseAPIUrlv3}form`;

  /**
   * Reads the amendment lock state for one form without acquiring or refreshing it.
   * An unlocked document resolves with `locked: false` rather than an error.
   */
  public getAmendmentLock(formId: number): Observable<AmendmentLock> {
    return this.http
      .get<unknown>(`${this.baseUrl}/${formId}/lock/`)
      .pipe(map((response) => parseAmendmentLock(response)));
  }

  /**
   * Opens an amendment session for the form, acquiring the document-level lock.
   * Re-acquiring a lock the current user already holds refreshes it.
   * Rejects with {@link AmendmentLockConflictError} when another user already holds it (409).
   */
  public acquireAmendmentLock(
    formId: number,
    sourceSurface: AmendmentLockSurface = 'rxphoto',
  ): Observable<AmendmentLock> {
    return this.http
      .post<unknown>(`${this.baseUrl}/${formId}/lock/`, { source_surface: sourceSurface })
      .pipe(
        map((response) => parseAmendmentLock(response)),
        rethrowAmendmentLockConflict(),
      );
  }

  /**
   * Refreshes the current user's active amendment lock so it does not expire from inactivity.
   * Rejects with {@link AmendmentLockConflictError} when the lock is now held by someone else (409).
   */
  public heartbeatAmendmentLock(formId: number): Observable<AmendmentLock> {
    return this.http.post<unknown>(`${this.baseUrl}/${formId}/amendment-heartbeat/`, null).pipe(
      map((response) => parseAmendmentLock(response)),
      rethrowAmendmentLockConflict(),
    );
  }
}

/**
 * Parses a 409 amendment-lock conflict body into a typed error.
 */
function parseAmendmentLockConflictError(value: unknown): AmendmentLockConflictError {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('Invalid amendment lock conflict response.');
  }

  const record = value as Record<string, unknown>;
  const lock = record['lock'] == null ? null : parseAmendmentLock(record['lock']);

  return new AmendmentLockConflictError(
    parseString(record['code']),
    parseString(record['detail']),
    lock,
  );
}

/**
 * Parses one amendment lock state record from the API response.
 * Holder-specific fields are optional so an unlocked document parses cleanly.
 */
function parseAmendmentLock(value: unknown): AmendmentLock {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('Invalid amendment lock response.');
  }

  const record = value as Record<string, unknown>;

  return {
    formId: parseNumber(record['form_id']),
    locked: parseBoolean(record['locked']),
    heldByCurrentUser: parseBoolean(record['held_by_current_user']),
    holderName: parseOptional(record['holder_name'], parseString),
    sourceVersionId: parseOptional(record['source_version_id'], parseNumber),
    sourceSurface: parseOptional(record['source_surface'], parseSurface),
    acquiredAt: parseOptional(record['acquired_at'], parseDate),
    lastActivityAt: parseOptional(record['last_activity_at'], parseDate),
    expiresAt: parseOptional(record['expires_at'], parseDate),
    remainingSeconds: parseOptional(record['remaining_seconds'], parseNumber) ?? 0,
    message: parseOptional(record['message'], parseString) ?? '',
  };
}

function parseOptional<T>(value: unknown, parse: (value: unknown) => T): T | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  return parse(value);
}

function parseNumber(value: unknown): number {
  if (typeof value !== 'number') {
    throw new Error('Invalid number.');
  }
  return value;
}

function parseBoolean(value: unknown): boolean {
  if (typeof value !== 'boolean') {
    throw new Error('Invalid boolean.');
  }
  return value;
}

function parseString(value: unknown): string {
  if (typeof value !== 'string') {
    throw new Error('Invalid string.');
  }
  return value;
}

function parseDate(value: unknown): Date {
  const date = new Date(parseString(value));

  if (Number.isNaN(date.getTime())) {
    throw new Error('Invalid date.');
  }

  return date;
}

function parseSurface(value: unknown): AmendmentLockSurface {
  if (value !== 'rxphoto' && value !== 'pne') {
    throw new Error('Invalid surface.');
  }
  return value;
}
